import React, { createContext, useState, useContext, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { db } from '@/api/db';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  useEffect(() => {
    // Safety timeout: if API hangs, unblock the app in 4s
    const timeout = setTimeout(() => {
      setIsLoadingAuth(false);
    }, 4000);

    // Check if we have a valid token + user
    const token = localStorage.getItem('portus_token');
    if (!token) {
      clearTimeout(timeout);
      setIsLoadingAuth(false);
      return;
    }

    // Validate token by calling /api/auth/me
    db.auth.me().then((userData) => {
      clearTimeout(timeout);
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        Sentry.setUser({ email: userData.email, id: userData.id });
      }
      setIsLoadingAuth(false);
    }).catch(() => {
      clearTimeout(timeout);
      setIsLoadingAuth(false);
    });

    return () => clearTimeout(timeout);
  }, []);

  const login = async (email, password) => {
    const result = await db.auth.login(email, password);
    setUser(result.user);
    setIsAuthenticated(true);
    return result;
  };

  const logout = async () => {
    localStorage.removeItem('portus_token');
    localStorage.removeItem('portus_user');
    Sentry.setUser(null);
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/login';
  };

  const navigateToLogin = () => {
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      login,
      logout,
      navigateToLogin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
